import { DateTime, DateTimeFormatOptions } from 'luxon';
import { Event } from '../types/Event';
import _ from 'lodash';

/**
 * Processes and formats date ranges for API calls.
 * Accepts both arrays of dates and objects with 'start' and 'end' properties.
 * @param range The date range to process.
 * @returns An object containing formatted start and end dates as ISO strings.
 */
export const formatDateRangeForAPI = (range: Date[] | { start: Date; end: Date }): { formattedStart: string; formattedEnd: string } => {
  let start, end;

  // Determine if the input is an array of dates or an object with start and end dates
  if (Array.isArray(range)) {
    start = range[0];
    end = range[range.length - 1];
  } else {
    start = range.start;
    end = range.end;
  }

  // Format the dates to ISO strings (date part only)

  const formattedStart = DateTime.fromJSDate(start).minus({ days: 3 }).toISO() ?? '';
  const formattedEnd = DateTime.fromJSDate(end).plus({ days: 3 }).toISO() ?? '';

  return { formattedStart, formattedEnd };
};

/**
 * Adds or subtracts a given number of days from a specified date.
 * If no date or days are provided, the function modifies the provided date by the given days.
 * @param date The ISO date string to start from (must be defined).
 * @param days The number of days to add (positive) or subtract (negative), defaults to 0.
 * @param humanize Whether to return the date in a human-readable format.
 *
 * @returns The resulting date as an ISO string or an empty string if the date is invalid.
 */
interface AddOrRemoveDaysToDateParams {
  date?: string;
  days?: number;
  humanize?: boolean;
}
export const addOrRemoveDaysToDate = ({ date, days = 0, humanize = true }: AddOrRemoveDaysToDateParams): string => {
  if (!date) {
    return String(days); // If date is undefined, return days as a string
  }

  // Use the provided date and modify it by the given number of days
  const startDate = DateTime.fromISO(date);

  if (!startDate.isValid) {
    return String(days); // If date is invalid, return days as a string
  }

  // Apply the day modification
  const modifiedDate = startDate.plus({ days });

  // Return the modified date in ISO format, or an empty string if invalid
  return humanize ? humanizeDate(modifiedDate.toISO()) : modifiedDate.toISO();
};

/**
 * Checks if an event is a single-day event.
 * An event is considered single-day if its end date is exactly one day after its start date.
 * @param event The event object with start_at and end_at properties.
 * @returns True if the event is a single-day event, otherwise false.
 */
export function singleDateEvent(event: Event): boolean {
  const startDt = DateTime.fromISO(event.start_at);
  const endDt = DateTime.fromISO(event.end_at);

  // Check if the end date is exactly one day after the start date
  return startDt.plus({ days: 1 }).equals(endDt);
}

/**
 * Formats the start and end date of an event into a human-readable string.
 * If the event is a single-day event, it returns just the start date.
 * If the showTime parameter is false, only the date portion is included.
 *
 * @param event The event object with start_at and end_at properties.
 * @returns A human-readable date string for the event.
 */
export function humanizeEventDateTimes(event: Event) {
  const startDt = DateTime.fromISO(event.start_at);

  if (singleDateEvent(event) || event.multi_parent) {
    return startDt.toLocaleString(DateTime.DATE_SHORT);
  }

  const endDt = DateTime.fromISO(event.end_at);
  if (startDt.toISODate() === endDt.toISODate()) {
    return `${startDt.toLocaleString(DateTime.DATETIME_SHORT)} - ${endDt.toLocaleString(DateTime.TIME_SIMPLE)}`;
  }
  return `${startDt.toLocaleString(DateTime.DATETIME_SHORT)} - ${endDt.toLocaleString(DateTime.DATETIME_SHORT)}`;
}

/**
 * Converts a given ISO date/time string into a localized format.
 * If the input is null, undefined, or not a valid ISO string, returns the provided default.
 *
 * @param dateStr - The ISO date/time string to format.
 * @param format - The DateTime format to use for output.
 * @param defaultStr - The fallback string to return if the date is invalid. Defaults to ' - '.
 * @returns A localized formatted date/time string, or the default string if invalid.
 */
function formatDateTime(dateStr: string | null | undefined, format: DateTimeFormatOptions, defaultStr = ' - '): string {
  if (!dateStr) return defaultStr;

  const parsed = DateTime.fromISO(dateStr);
  if (!parsed.isValid) return defaultStr;

  return parsed.toLocaleString(format);
}

/**
 * Converts a given ISO date string into a localized short date format.
 * If the input is null, undefined, or not a valid ISO string, returns the provided default.
 *
 * @param dateStr - The ISO date string to format.
 * @param defaultStr - The fallback string to return if the date is invalid. Defaults to ' - '.
 * @returns A localized short date string, or the default string if invalid.
 */
export function humanizeDate(dateStr: string | null | undefined, defaultStr = ' - '): string {
  return formatDateTime(dateStr, DateTime.DATE_SHORT, defaultStr);
}

/**
 * Converts a given ISO timestamp string into a localized short date and time format.
 * If the input is null, undefined, or not a valid ISO string, returns the provided default.
 *
 * @param dateStr - The ISO timestamp string to format.
 * @param defaultStr - The fallback string to return if the timestamp is invalid. Defaults to ' - '.
 * @returns A localized short date and time string, or the default string if invalid.
 */
export function humanizeTimestamp(dateStr: string | null | undefined, defaultStr = ' - '): string {
  return formatDateTime(dateStr, DateTime.DATETIME_SHORT, defaultStr);
}

export function parseIsoWithDefault(dateStr: string | null | undefined, defValue: any = null) {
  if (!dateStr) return defValue;

  const dtVal = DateTime.fromISO(dateStr);
  return dtVal.isValid ? dtVal : defValue;
}

/**
 * Returns the full 7-day week (Sun–Sat) that contains the given date
 * @param date The date to get the week range from
 * @returns An array of 7 dates representing the week
 */
export function getWeekRangeFromDate(date: Date): Date[] {
  const base = DateTime.fromJSDate(date).toLocal();
  // Force Sunday start to match react-big-calendar
  const start = base.minus({ days: base.weekday % 7 }).startOf('day');
  return Array.from({ length: 7 }, (_, i) => start.plus({ days: i }).toJSDate());
}
